import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { toast } from "@/components/ui/sonner";
import {
  createPartyMutation as createPartyMutationOptions,
  createQuestionOptionMutation,
  getAllLicenseTypeQuestionsOptions,
  getPartyOptions,
  getQuestionOptionsOptions,
  updatePartyMutation as updatePartyMutationOptions,
  updateQuestionOptionMutation,
} from "@/hera-api/client/@tanstack/react-query.gen";
import type {
  PartyResponseSchema,
  QuestionOptionResponseSchema,
  QuestionSummaryForOptionSchema,
} from "@/hera-api/client/types.gen";
import type { HelpPortalId } from "@/lib/help-portal";
import { isNacStyleAnswerType } from "@/lib/nac-answer-types";
import { omtAmsImpersonatePortalHref } from "@/lib/omt-ams-impersonate";
import { invalidatePartiesAndQuestionOptions } from "@/lib/parties-query-invalidation";
import { getQueryErrorHeading, getQueryErrorMessage } from "@/lib/utils";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ExternalLink, Loader2, Trash2 } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Button } from "../ui/button";
import ErrorComponent from "../ui/error";
import LoadingScreen from "../ui/loading-screen";
import { Select } from "../ui/select";
import type { PartyFormValues } from "./party-form";
import { PartyForm } from "./party-form";

interface PartyDetailsSheetProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  nacId: string | null;
  /** Government users can view but not write party or question-option data. */
  readOnly?: boolean;
  /** Show Impersonate that opens the OMT/AMS portal as this party. Operator portal only. */
  showImpersonateButton?: boolean;
  /** Help Center the Exempt switch opens. */
  helpPortal?: HelpPortalId;
}

function formatQuestionContext(
  summary: QuestionSummaryForOptionSchema,
): string {
  const parts: string[] = [
    summary.question_type,
    summary.scope,
    summary.license_type_name,
  ];
  if (summary.jurisdiction_name) parts.push(summary.jurisdiction_name);
  if (summary.question_group_name) parts.push(summary.question_group_name);
  return parts.join(" · ");
}

export function PartyDetailsSheet({
  isOpen,
  onOpenChange,
  nacId,
  readOnly = false,
  showImpersonateButton = false,
  helpPortal = "operator",
}: PartyDetailsSheetProps) {
  const queryClient = useQueryClient();
  const [addQuestionValue, setAddQuestionValue] = useState<string[]>([]);
  const [pendingAddQuestionIds, setPendingAddQuestionIds] = useState<string[]>(
    [],
  );
  const [pendingRemoveOptionIds, setPendingRemoveOptionIds] = useState<
    string[]
  >([]);

  const isCreate = nacId == null;

  // Reset pending question add/remove when panel opens or party changes so
  // Cancel discards them and we never apply one party's pending changes to another.
  // biome-ignore lint/correctness/useExhaustiveDependencies: reset when panel opens or party (nacId) changes
  useEffect(() => {
    setPendingAddQuestionIds([]);
    setPendingRemoveOptionIds([]);
    setAddQuestionValue([]);
  }, [isOpen, nacId]);

  const {
    data: partyData,
    isLoading: isPartyLoading,
    error: partyError,
  } = useQuery({
    ...getPartyOptions({
      path: { party_id: nacId ?? "" },
    }),
    enabled: isOpen && !!nacId,
  });

  const { data: optionsData, isLoading: isOptionsLoading } = useQuery({
    ...getQuestionOptionsOptions({
      query: {
        party_id: nacId ?? undefined,
        include_deleted: true,
      },
    }),
    enabled: isOpen && !!nacId,
  });

  const { data: allQuestionsData } = useQuery({
    ...getAllLicenseTypeQuestionsOptions({}),
    enabled: isOpen,
  });

  const partyFormValues: PartyFormValues | null = useMemo(() => {
    if (!partyData) return null;
    const p = partyData as PartyResponseSchema;
    return {
      name: p.name ?? "",
      company_name: p.company_name ?? "",
      email: p.email ?? "",
      phone: p.phone ?? "",
      address1: p.address1 ?? "",
      address2: p.address2 ?? "",
      city: p.city ?? "",
      state: p.state ?? "",
      zipcode: p.zipcode ?? "",
      is_active: p.deleted_at == null,
      is_exempt: p.is_exempt ?? false,
    };
  }, [partyData]);

  const questionOptions = optionsData?.question_options ?? [];

  const fixedListQuestions = useMemo(() => {
    const list = allQuestionsData?.questions ?? [];
    return list.filter(
      (q) =>
        isNacStyleAnswerType(q.answer_type) &&
        q.select_from_fixed_list === true,
    );
  }, [allQuestionsData]);

  const displayQuestionIds = useMemo(() => {
    const ids = new Set(pendingAddQuestionIds);
    for (const o of questionOptions) {
      if (!o.deleted_at && !pendingRemoveOptionIds.includes(o.id)) {
        ids.add(o.question_id);
      }
    }
    return ids;
  }, [questionOptions, pendingAddQuestionIds, pendingRemoveOptionIds]);

  const addQuestionSelectOptions = useMemo(() => {
    return fixedListQuestions
      .filter((q) => !displayQuestionIds.has(q.id))
      .map((q) => ({ value: q.id, label: q.question }));
  }, [fixedListQuestions, displayQuestionIds]);

  const questionsById = useMemo(() => {
    const map = new Map(
      (allQuestionsData?.questions ?? []).map((q) => [q.id, q]),
    );
    return map;
  }, [allQuestionsData]);

  const createPartyMut = useMutation({
    ...createPartyMutationOptions(),
    onSuccess: () => {
      invalidatePartiesAndQuestionOptions(queryClient);
    },
    onError: (error) => {
      toast({
        title: getQueryErrorHeading(error),
        description: getQueryErrorMessage(error),
        variant: "destructive",
      });
    },
  });

  const updatePartyMut = useMutation({
    ...updatePartyMutationOptions(),
    onSuccess: () => {
      invalidatePartiesAndQuestionOptions(queryClient);
    },
    onError: (error) => {
      toast({
        title: getQueryErrorHeading(error),
        description: getQueryErrorMessage(error),
        variant: "destructive",
      });
    },
  });

  const createOptionMutation = useMutation({
    ...createQuestionOptionMutation(),
    onSuccess: () => {
      invalidatePartiesAndQuestionOptions(queryClient);
    },
    onError: (error) => {
      toast({
        title: getQueryErrorHeading(error),
        description: getQueryErrorMessage(error),
        variant: "destructive",
      });
    },
  });

  const updateOptionMutation = useMutation({
    ...updateQuestionOptionMutation(),
    onSuccess: () => {
      invalidatePartiesAndQuestionOptions(queryClient);
    },
    onError: (error) => {
      toast({
        title: getQueryErrorHeading(error),
        description: getQueryErrorMessage(error),
        variant: "destructive",
      });
    },
  });

  const handlePartySubmit = async (values: PartyFormValues) => {
    let resolvedNacId: string | null = nacId;
    if (isCreate) {
      const result = await createPartyMut.mutateAsync({
        body: {
          name: values.name || null,
          company_name: values.company_name || null,
          email: values.email || null,
          phone: values.phone || null,
          address1: values.address1 || null,
          address2: values.address2 || null,
          city: values.city || null,
          state: values.state || null,
          zipcode: values.zipcode || null,
          is_exempt: values.is_exempt,
        },
      });
      resolvedNacId = result.id;
    } else if (nacId) {
      await updatePartyMut.mutateAsync({
        path: { party_id: nacId },
        body: {
          name: values.name || null,
          company_name: values.company_name || null,
          email: values.email || null,
          phone: values.phone || null,
          address1: values.address1 || null,
          address2: values.address2 || null,
          city: values.city || null,
          state: values.state || null,
          zipcode: values.zipcode || null,
          is_deleted: !values.is_active,
          is_exempt: values.is_exempt,
        },
      });
    }
    if (resolvedNacId) {
      for (const questionId of pendingAddQuestionIds) {
        await createOptionMutation.mutateAsync({
          body: { question_id: questionId, party_id: resolvedNacId },
        });
      }
      for (const optionId of pendingRemoveOptionIds) {
        await updateOptionMutation.mutateAsync({
          path: { option_id: optionId },
          body: { is_deleted: true },
        });
      }
    }
    await invalidatePartiesAndQuestionOptions(queryClient, { refetch: true });
    setPendingAddQuestionIds([]);
    setPendingRemoveOptionIds([]);
    setAddQuestionValue([]);
    toast({
      title: isCreate ? "Party created" : "Party updated",
    });
    onOpenChange(false);
  };

  const handleAddQuestion = (selected: string[]) => {
    const questionId = selected[0];
    if (!questionId) return;
    setPendingAddQuestionIds((prev) =>
      prev.includes(questionId) ? prev : [...prev, questionId],
    );
    setAddQuestionValue([]);
  };

  const handleRemoveOption = (
    opt: QuestionOptionResponseSchema | { optionId: null; questionId: string },
  ) => {
    if ("questionId" in opt && opt.optionId === null) {
      setPendingAddQuestionIds((prev) =>
        prev.filter((id) => id !== opt.questionId),
      );
    } else if ("id" in opt) {
      setPendingRemoveOptionIds((prev) =>
        prev.includes(opt.id) ? prev : [...prev, opt.id],
      );
    }
  };

  const impersonateEmail =
    !isCreate && partyFormValues?.email?.trim()
      ? partyFormValues.email.trim()
      : "";

  const isLoading = isCreate ? false : isPartyLoading;
  const isSaving =
    createPartyMut.isPending ||
    updatePartyMut.isPending ||
    createOptionMutation.isPending ||
    updateOptionMutation.isPending;

  const displayItems: Array<
    | { type: "option"; opt: QuestionOptionResponseSchema }
    | { type: "pending"; questionId: string }
  > = useMemo(() => {
    const items: Array<
      | { type: "option"; opt: QuestionOptionResponseSchema }
      | { type: "pending"; questionId: string }
    > = [];
    if (!isCreate && nacId) {
      for (const o of questionOptions) {
        if (!o.deleted_at && !pendingRemoveOptionIds.includes(o.id)) {
          items.push({ type: "option", opt: o });
        }
      }
    }
    for (const questionId of pendingAddQuestionIds) {
      items.push({ type: "pending", questionId });
    }
    return items;
  }, [
    isCreate,
    nacId,
    questionOptions,
    pendingRemoveOptionIds,
    pendingAddQuestionIds,
  ]);

  const questionsSection =
    allQuestionsData != null ? (
      <div className="space-y-3">
        <div>
          <h3 className="text-sm font-medium">
            {isCreate
              ? "Questions to link to this party"
              : "Questions using this party"}
          </h3>
          <p className="text-xs text-muted-foreground mt-0.5">
            {readOnly
              ? "Questions linked to this party."
              : "Add or remove questions here; changes are saved only when you press Save. Cancel discards them."}
          </p>
        </div>
        {!isCreate && isOptionsLoading ? (
          <div className="flex items-center gap-2 text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading…
          </div>
        ) : (
          <>
            <ul className="space-y-2">
              {displayItems.map((item) => {
                if (item.type === "option") {
                  const o = item.opt;
                  return (
                    <li
                      key={o.id}
                      className="flex items-start justify-between gap-2 rounded-md border p-3 text-sm"
                    >
                      <div>
                        <div className="font-medium">
                          {o.question_summary?.question ?? "Question"}
                        </div>
                        {o.question_summary && (
                          <div className="text-muted-foreground mt-0.5">
                            {formatQuestionContext(o.question_summary)}
                          </div>
                        )}
                      </div>
                      {readOnly ? null : (
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={() => handleRemoveOption(o)}
                          disabled={isSaving}
                          aria-label="Remove question from party"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </li>
                  );
                }
                const q = questionsById.get(item.questionId);
                return (
                  <li
                    key={`pending-${item.questionId}`}
                    className="flex items-start justify-between gap-2 rounded-md border p-3 text-sm"
                  >
                    <div>
                      <div className="font-medium">
                        {q?.question ?? "Question"}
                      </div>
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() =>
                        handleRemoveOption({
                          optionId: null,
                          questionId: item.questionId,
                        })
                      }
                      disabled={isSaving}
                      aria-label="Remove question from list"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </li>
                );
              })}
              {displayItems.length === 0 && (
                <li className="text-muted-foreground text-sm">
                  {isCreate
                    ? "No questions selected. Add one below (saved when you click Save)."
                    : "No questions linked. Add one below."}
                </li>
              )}
            </ul>
            {!readOnly && addQuestionSelectOptions.length > 0 && (
              <div className="flex items-center gap-2">
                <Select
                  options={addQuestionSelectOptions}
                  value={addQuestionValue}
                  onValueChange={(v) => {
                    setAddQuestionValue(v);
                    if (v.length > 0) handleAddQuestion(v);
                  }}
                  placeholder="Add question…"
                  searchPlaceholder="Search questions…"
                  emptyMessage="No questions available"
                  className="flex-1"
                  multiple={false}
                />
              </div>
            )}
          </>
        )}
      </div>
    ) : null;

  return (
    <Sheet open={isOpen} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-2xl">
        <SheetHeader>
          <div className="flex items-start justify-between gap-2 pr-8">
            <div className="min-w-0">
              <SheetTitle>
                {isCreate ? "Add New Party" : readOnly ? "Party" : "Edit Party"}
              </SheetTitle>
              <SheetDescription>
                {isCreate
                  ? "Create a new party (name/address/contact)."
                  : readOnly
                    ? "View party information and linked questions."
                    : "Edit party information and linked questions."}
              </SheetDescription>
            </div>
            {showImpersonateButton && impersonateEmail ? (
              <Button
                size="sm"
                variant="outline"
                className="shrink-0"
                aria-label="Impersonate party"
                onClick={() =>
                  window.open(
                    omtAmsImpersonatePortalHref(impersonateEmail),
                    "_blank",
                  )
                }
              >
                <ExternalLink className="h-4 w-4 mr-2" />
                Impersonate
              </Button>
            ) : null}
          </div>
        </SheetHeader>
        <div className="overflow-y-auto max-h-[calc(100vh-120px)] px-4 space-y-6">
          {isLoading ? (
            <div className="flex justify-center items-center py-8">
              <LoadingScreen />
            </div>
          ) : partyError ? (
            <ErrorComponent
              title={getQueryErrorHeading(partyError)}
              message={getQueryErrorMessage(partyError)}
            />
          ) : (
            <PartyForm
              key={nacId ?? "new"}
              partyData={partyFormValues}
              onSubmit={handlePartySubmit}
              onCancel={() => onOpenChange(false)}
              isSubmitting={isSaving}
              readOnly={readOnly}
              helpPortal={helpPortal}
            >
              {questionsSection}
            </PartyForm>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
